import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Segmented, Spin, Tag } from "antd";
import { ArrowLeftOutlined, CommentOutlined, GlobalOutlined, InstagramOutlined, ReloadOutlined, UserOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { connectedChannels } from "./channelKind";
import { groupSessionsByDate } from "util/copilotSessionGroups";
import {
  getProspectSession,
  listProspectLeads,
  listProspectSessions,
  mapProspectLeadRow,
  mapProspectSessionRow,
} from "./prospectService";
import IntentInsightsTab from "./intentInsights/IntentInsightsTab";
import { trackProspectProductMilestone } from "util/prospectTrialAnalytics";
import SalesHandoffModal from "./SalesHandoffModal";
import { buildHandoffSummaryFromSession } from "./salesHandoffService";
import insightStyles from "./intentInsights/intentInsights.module.scss";
import styles from "./style.module.scss";

const GROUP_I18N = {
  today: "sonic_prospect.history_group_today",
  yesterday: "sonic_prospect.history_group_yesterday",
  last_week: "sonic_prospect.history_group_last_week",
  older: "sonic_prospect.history_group_older",
};

function formatTime(ts, locale) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      day: "numeric",
      month: "short",
    });
  } catch (_) {
    return "";
  }
}

function sessionChannel(value) {
  const raw = String(value || "website").toLowerCase();
  if (raw.includes("instagram")) return "instagram";
  if (raw.includes("whatsapp")) return "whatsapp";
  return "web";
}

const CHANNEL_MARK = {
  web: GlobalOutlined,
  instagram: InstagramOutlined,
  whatsapp: WhatsAppOutlined,
};

function shortSessionId(id) {
  const value = String(id || "");
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function LeadProfileCard({ lead, sessionId, t, locale }) {
  if (!lead?.email) return null;
  const sectorKey = lead.sector
    ? `sonic_prospect.lead_sector_${lead.sector}`
    : null;
  const sectorLabel = lead.sector === "other" && lead.sector_other_text
    ? lead.sector_other_text
    : sectorKey
      ? t(sectorKey, { defaultValue: lead.sector })
      : "—";
  const rangeLabels = {
    "<10": "sonic_prospect.lead_range_lt10",
    "10-250": "sonic_prospect.lead_range_10_250",
    "250-1000": "sonic_prospect.lead_range_250_1000",
    "1000-10000": "sonic_prospect.lead_range_1000_10000",
    "10000+": "sonic_prospect.lead_range_10000_plus",
  };
  const rangeKey = lead.customer_range ? rangeLabels[lead.customer_range] : null;
  const rangeLabel = rangeKey
    ? t(rangeKey, { defaultValue: lead.customer_range })
    : "—";

  return (
    <div className={styles.leadCard}>
      <div className={styles.leadCardHead}>
        <UserOutlined />
        <strong>{t("sonic_prospect.lead_profile_title")}</strong>
        {lead.segment ? (
          <Tag color={lead.segment === "Enterprise" ? "purple" : "blue"}>{lead.segment}</Tag>
        ) : null}
      </div>
      <dl className={styles.leadGrid}>
        <div>
          <dt>{t("sonic_prospect.lead_email")}</dt>
          <dd>{lead.email}</dd>
        </div>
        <div>
          <dt>{t("sonic_prospect.lead_sector")}</dt>
          <dd>{sectorLabel}</dd>
        </div>
        <div>
          <dt>{t("sonic_prospect.lead_customer_range")}</dt>
          <dd>{rangeLabel}</dd>
        </div>
        <div>
          <dt>{t("sonic_prospect.lead_session_id")}</dt>
          <dd title={sessionId}>{shortSessionId(sessionId)}</dd>
        </div>
        {lead.trigger_message ? (
          <div className={styles.leadGridWide}>
            <dt>{t("sonic_prospect.lead_trigger")}</dt>
            <dd>{lead.trigger_message}</dd>
          </div>
        ) : null}
        {lead.captured_at ? (
          <div>
            <dt>{t("sonic_prospect.lead_captured_at")}</dt>
            <dd>{formatTime(lead.captured_at, locale)}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

const BotConversations = ({
  bot,
  onBack,
  prospectLimits = null,
  initialTab = "all",
  embedded = false,
  setupPending = false,
  onGetEmbed,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("tr") ? "tr-TR" : "en-US";
  const brandColor = bot?.appearance?.brandColor || "#7c3aed";
  const salesAgentAvailable = Boolean(prospectLimits?.sales_agent_available);
  const [viewMode, setViewMode] = useState(initialTab);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const showChannelFilter = connectedChannels(bot).length > 1;

  const loadList = useCallback(async () => {
    if (!bot?.id) return;
    setLoading(true);
    try {
      const [sessionRows, leadRows] = await Promise.all([
        listProspectSessions(bot.id),
        listProspectLeads(bot.id),
      ]);
      const mappedSessions = sessionRows.map(mapProspectSessionRow);
      const mappedLeads = leadRows.map(mapProspectLeadRow);
      setSessions(mappedSessions);
      setLeads(mappedLeads);
      if (mappedSessions.length > 0) {
        trackProspectProductMilestone("first_conversation", { bot_id: bot.id });
      }
      setSelectedId((prev) => {
        const ids = mappedSessions.map((row) => row.id);
        if (prev && ids.includes(prev)) return prev;
        return ids[0] || null;
      });
    } catch (_) {
      setSessions([]);
      setLeads([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [bot?.id]);

  useEffect(() => {
    setViewMode(initialTab);
  }, [initialTab, bot?.id]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const pool = viewMode === "leads" ? leads : sessions;
    const ids = pool.map((row) => (viewMode === "leads" ? row.sessionId : row.id));
    setSelectedId((prev) => {
      if (prev && ids.includes(prev)) return prev;
      return ids[0] || null;
    });
  }, [viewMode, leads, sessions]);

  useEffect(() => {
    if (!bot?.id || !selectedId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    getProspectSession(bot.id, selectedId)
      .then((session) => {
        if (!cancelled) setDetail(session);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bot?.id, selectedId]);

  const railItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (viewMode === "leads") {
      const rows = q
        ? leads.filter((row) => {
            const hay = [
              row.title,
              row.lead?.email,
              row.lead?.segment,
              row.sessionId,
              row.ip,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(q);
          })
        : leads;
      return rows.map((row) => ({
        id: row.sessionId,
        title: row.lead?.email || row.title || t("sonic_prospect.conversations_untitled"),
        subtitle: [
          row.lead?.segment,
          row.capturedAt ? formatTime(row.capturedAt, locale) : formatTime(row.updatedAt, locale),
          row.ip,
        ]
          .filter(Boolean)
          .join(" · "),
        badge: row.lead?.segment || "",
      }));
    }
    return [];
  }, [viewMode, leads, search, t, locale]);

  const groupedRail = useMemo(() => {
    if (viewMode === "leads") return [];
    const q = search.trim().toLowerCase();
    const rows = sessions.filter((s) => {
      if (channelFilter !== "all" && sessionChannel(s.channel) !== channelFilter) return false;
      if (!q) return true;
      const hay = [s.title, s.leadEmail, s.ip, s.id].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    return groupSessionsByDate(rows);
  }, [viewMode, sessions, search, channelFilter]);

  const messages = detail?.messages || [];

  return (
    <div className={`${styles.convPage}${embedded ? ` ${styles.embeddedPane}` : ""}`}>
      {embedded ? null : (
      <header className={styles.pageHeader}>
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
            {t("sonic_prospect.btn_back")}
          </Button>
          <h1>{t("sonic_prospect.conversations_title")}</h1>
          <p>
            {bot?.name || t("sonic_prospect.preview_bot_fallback")} ·{" "}
            {t("sonic_prospect.conversations_subtitle")}
          </p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadList}>
          {t("sonic_prospect.btn_refresh")}
        </Button>
      </header>
      )}

      <div className={styles.convToolbar}>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { label: t("sonic_prospect.conversations_tab_all"), value: "all" },
            {
              label: t("sonic_prospect.conversations_tab_leads", { count: leads.length }),
              value: "leads",
            },
            ...(embedded
              ? []
              : [
                  {
                    label: (
                      <span>
                        {t("sonic_prospect.intent_insights.tab_label")}
                        <span className={insightStyles.tabNewBadge}>NEW</span>
                      </span>
                    ),
                    value: "insights",
                  },
                ]),
          ]}
        />
      </div>

      {viewMode === "insights" ? (
        <IntentInsightsTab
          botId={bot?.id}
          botName={bot?.title || bot?.name}
          onViewAllChats={() => setViewMode("all")}
        />
      ) : sessions.length === 0 && !loading ? (
        <div className={styles.convEmptyFull}>
          <CommentOutlined />
          <h2>{t("sonic_prospect.conversations_empty_title")}</h2>
          <p>{t("sonic_prospect.conversations_empty")}</p>
          {setupPending && onGetEmbed ? (
            <Button type="primary" onClick={onGetEmbed}>
              {t("sonic_prospect.setup_finish")}
            </Button>
          ) : null}
        </div>
      ) : (
      <div className={styles.convLayout}>
        <aside className={styles.convRail}>
          {showChannelFilter && viewMode !== "leads" ? (
            <Segmented
              value={channelFilter}
              onChange={setChannelFilter}
              options={[
                { label: t("sonic_prospect.channel_filter_all"), value: "all" },
                { label: t("sonic_prospect.channel_name_web"), value: "web" },
                { label: t("sonic_prospect.channel_name_instagram"), value: "instagram" },
                { label: t("sonic_prospect.channel_name_whatsapp"), value: "whatsapp" },
              ]}
            />
          ) : null}
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              viewMode === "leads"
                ? t("sonic_prospect.leads_search")
                : t("sonic_prospect.conversations_search")
            }
          />
          {loading ? (
            <div className={styles.centered}>
              <Spin />
            </div>
          ) : viewMode === "leads" && leads.length === 0 ? (
            <div className={styles.convEmpty}>
              <CommentOutlined />
              <p>{t("sonic_prospect.leads_empty")}</p>
            </div>
          ) : viewMode === "leads" ? (
            railItems.map((item) => (
              <button
                type="button"
                key={item.id}
                className={
                  item.id === selectedId ? styles.convSessionActive : styles.convSession
                }
                onClick={() => setSelectedId(item.id)}
              >
                <span className={styles.convSessionTitle}>{item.title}</span>
                <span className={styles.convSessionMeta}>{item.subtitle}</span>
                {item.badge ? (
                  <Tag className={styles.convLeadTag} color="purple">
                    {item.badge}
                  </Tag>
                ) : null}
              </button>
            ))
          ) : (
            groupedRail.map((group) => (
              <div key={group.key}>
                <div className={styles.convGroupLabel}>{t(GROUP_I18N[group.key])}</div>
                {group.sessions.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      className={
                        session.id === selectedId
                          ? styles.convSessionActive
                          : styles.convSession
                      }
                      onClick={() => setSelectedId(session.id)}
                    >
                      {(() => {
                        const Mark = CHANNEL_MARK[sessionChannel(session.channel)];
                        return <Mark className={styles.convChannelIcon} />;
                      })()}
                      <span className={styles.convSessionTitle}>
                        {session.leadEmail ||
                          session.title ||
                          t("sonic_prospect.conversations_untitled")}
                      </span>
                      <span className={styles.convSessionMeta}>
                        {t("sonic_prospect.conversations_messages", {
                          count: session.messageCount,
                        })}
                        {session.updatedAt
                          ? ` · ${formatTime(session.updatedAt, locale)}`
                          : ""}
                        {session.ip ? ` · ${session.ip}` : ""}
                        {((sessionChannel(session.channel) === "instagram" &&
                          bot?.instagram?.removed &&
                          !bot?.instagram?.connected) ||
                          (sessionChannel(session.channel) === "whatsapp" &&
                            bot?.whatsapp?.removed &&
                            !bot?.whatsapp?.connected))
                          ? ` · ${t("sonic_prospect.channel_removed_note")}`
                          : ""}
                      </span>
                      {session.leadEmail ? (
                        <Tag className={styles.convLeadTag} color="blue">
                          {t("sonic_prospect.lead_badge")}
                        </Tag>
                      ) : null}
                    </button>
                  ))}
              </div>
            ))
          )}
        </aside>

        <section className={styles.convTranscript}>
          {!selectedId ? (
            <div className={styles.convEmpty}>
              <p>{t("sonic_prospect.conversations_pick")}</p>
            </div>
          ) : detailLoading ? (
            <div className={styles.centered}>
              <Spin />
            </div>
          ) : (
            <div className={styles.convTranscriptScroll}>
              <LeadProfileCard lead={detail?.lead} sessionId={selectedId} t={t} locale={locale} />
              <div className={styles.convTranscriptHead}>
                <div>
                  {detail?.lead?.email ||
                    detail?.title ||
                    t("sonic_prospect.conversations_untitled")}
                </div>
                <div className={styles.convTranscriptMeta}>
                  {t("sonic_prospect.lead_session_id")}: {shortSessionId(selectedId)}
                  {" · "}
                  {detail?.ip
                    ? t("sonic_prospect.conversations_ip", { ip: detail.ip })
                    : t("sonic_prospect.conversations_ip_unknown")}
                  {detail?.rating === "good"
                    ? ` · ${t("sonic_prospect.conversations_rating_good")}`
                    : ""}
                  {detail?.rating === "bad"
                    ? ` · ${t("sonic_prospect.conversations_rating_bad")}`
                    : ""}
                  {detail?.csat?.ease
                    ? ` · ${t("sonic_prospect.conversations_csat_ease", {
                        ease: t(
                          `sonic_prospect.conversations_csat_${detail.csat.ease}`,
                          { defaultValue: detail.csat.ease }
                        ),
                      })}`
                    : ""}
                  {detail?.csat?.comment
                    ? ` · ${t("sonic_prospect.conversations_csat_comment", {
                        comment: detail.csat.comment,
                      })}`
                    : ""}
                </div>
                {!salesAgentAvailable && selectedId ? (
                  <p className={styles.pdfHint}>{t("sonic_prospect.sales_handoff.agent_unavailable_admin")}</p>
                ) : null}
                {salesAgentAvailable && selectedId ? (
                  <Button type="primary" size="small" onClick={() => setHandoffOpen(true)}>
                    {t("sonic_prospect.sales_handoff.cta")}
                  </Button>
                ) : null}
              </div>
              <div className={styles.convBubbles}>
                {messages.map((msg, i) => (
                  <div
                    key={`${msg.ts || i}-${i}`}
                    className={
                      msg.role === "user" ? styles.convBubbleUser : styles.convBubbleBot
                    }
                    style={
                      msg.role === "assistant"
                        ? { background: `${brandColor}18`, borderColor: `${brandColor}44` }
                        : undefined
                    }
                  >
                    <span className={styles.convBubbleRole}>
                      {msg.role === "user"
                        ? t("sonic_prospect.conversations_visitor")
                        : bot?.name || t("sonic_prospect.preview_bot_fallback")}
                      {msg.ts ? ` · ${formatTime(msg.ts, locale)}` : ""}
                    </span>
                    <p>{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
      )}

      <SalesHandoffModal
        open={handoffOpen}
        onClose={() => setHandoffOpen(false)}
        botId={bot?.id}
        sessionId={selectedId}
        visitorEmail={detail?.lead?.email}
        intent={detail?.lead?.notes ? "Lead captured" : "General"}
        friction={detail?.lead?.friction}
        summary={buildHandoffSummaryFromSession(detail, messages)}
        onSuccess={() => setHandoffOpen(false)}
      />
    </div>
  );
};

export default BotConversations;
